import { useId } from "react";

import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";

import { AppointmentExchangeResponse, AppointmentExchangeStatus } from "@/api";
import { EditExchangeRequestDialog } from "@/pages/exchange/components/edit-exchange-request-dialog/EditExchangeRequestDialog";
import { useTranslation } from "@/translations";

import { ExchangeOfferCard } from "./components/ExchangeOfferCard";
import { ExchangeRequestSummary } from "./components/ExchangeRequestSummary";
import { useExchangeOffers } from "./hooks/useExchangeOffers";

interface Props {
  exchange: AppointmentExchangeResponse;
  onClose: () => void;
  onDeleteRequest: (row: AppointmentExchangeResponse) => void;
  // The deletion runs on the page's mutation, which the dialog's own `isBusy` does not see.
  isDeletingRequest: boolean;
}

/**
 * "Angebote zur Tauschanfrage": the offers received on an own Anfrage, open ones first with their two
 * decisions, decided ones below under "Erledigt". Stays open after a decision so the outcome is visible;
 * "Bearbeiten" and "Anfrage löschen" are offered only while the request (Anfrage) is still open.
 */
export function ExchangeOffersDialog({ exchange, onClose, onDeleteRequest, isDeletingRequest }: Props) {
  const { t } = useTranslation();
  const titleId = useId();
  const {
    request,
    pending,
    processed,
    isLoading,
    isError,
    isBusy,
    accept,
    reject,
    isEditRequestOpen,
    openEditRequest,
    closeEditRequest,
  } = useExchangeOffers(exchange);

  const isRequestOpen = request.status === AppointmentExchangeStatus.Open;
  const hasOffers = pending.length + processed.length > 0;

  return (
    <>
      <Dialog open onClose={onClose} maxWidth="md" fullWidth aria-labelledby={titleId}>
        <DialogTitle
          id={titleId}
          variant="h1"
          sx={{ p: 5, pb: 3, display: "flex", alignItems: "center", justifyContent: "space-between" }}
        >
          {t("exchange.offersDialog.title")}
          <IconButton aria-label={t("common.close")} onClick={onClose}>
            <CloseRoundedIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 5 }}>
          <Stack spacing={3}>
            <ExchangeRequestSummary exchange={request} />

            {isLoading ? (
              <Box display="flex" justifyContent="center" py={4}>
                <CircularProgress aria-label={t("exchange.offersDialog.loading")} />
              </Box>
            ) : isError ? (
              <Alert severity="error">{t("exchange.offersDialog.loadError")}</Alert>
            ) : !hasOffers ? (
              <Typography color="text.secondary">{t("exchange.offersDialog.empty")}</Typography>
            ) : (
              <>
                {pending.length > 0 && (
                  <Stack spacing={1.5} aria-busy={isBusy}>
                    {pending.map((offer) => (
                      <ExchangeOfferCard
                        key={offer.id}
                        offer={offer}
                        isBusy={isBusy}
                        onAccept={accept}
                        onReject={reject}
                      />
                    ))}
                  </Stack>
                )}
                {processed.length > 0 && (
                  <Box>
                    <Typography variant="overline" component="h3" color="text.secondary" sx={{ mb: 1 }}>
                      {t("exchange.offersDialog.processedSection")}
                    </Typography>
                    <Stack spacing={1.5}>
                      {processed.map((offer) => (
                        <ExchangeOfferCard key={offer.id} offer={offer} />
                      ))}
                    </Stack>
                  </Box>
                )}
              </>
            )}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 5, pt: 0, gap: 2, justifyContent: "space-between" }}>
          <Box sx={{ display: "flex", gap: 2 }}>
            <Button variant="outlined" onClick={onClose}>
              {t("common.close")}
            </Button>
            {isRequestOpen && (
              <Button
                variant="outlined"
                color="error"
                startIcon={<DeleteOutlineRoundedIcon />}
                disabled={isBusy || isDeletingRequest}
                onClick={() => onDeleteRequest(request)}
              >
                {t("exchange.actions.deleteRequest")}
              </Button>
            )}
          </Box>
          {isRequestOpen && (
            <Button
              variant="outlined"
              startIcon={<EditRoundedIcon />}
              disabled={isBusy || isDeletingRequest}
              onClick={openEditRequest}
            >
              {t("common.edit")}
            </Button>
          )}
        </DialogActions>
      </Dialog>
      {isEditRequestOpen && isRequestOpen && (
        <EditExchangeRequestDialog exchange={request} onClose={closeEditRequest} />
      )}
    </>
  );
}
